import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white dark:bg-fraudeye-primary">
      <div className="container max-w-4xl px-4 py-12">
        <Button variant="outline" size="sm" className="mb-8" asChild>
          <Link to="/">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Link>
        </Button>
        
        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-muted-foreground">Last updated: April 14, 2025</p>
          </div>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">1. Introduction</h2>
            <p>
              FraudEye ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our fraud detection service.
            </p>
            <p>
              Please read this Privacy Policy carefully. By accessing or using our service, you acknowledge that you have read, understood, and agree to be bound by all the terms outlined in this policy.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">2. Information We Collect</h2>
            <h3 className="text-lg font-medium">2.1 Personal Information</h3>
            <p>
              We may collect personally identifiable information, such as:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Name and email address when you create an account</li>
              <li>Billing information when you subscribe to our services</li>
              <li>Information you provide when contacting our support team</li>
            </ul>
            
            <h3 className="text-lg font-medium">2.2 Content for Analysis</h3>
            <p>
              When you use our fraud detection services, we collect and process the content you submit for analysis, which may include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Text documents and written content</li>
              <li>Images and photographs</li>
              <li>Video and audio recordings</li>
              <li>Other files and documents</li>
            </ul>
            
            <h3 className="text-lg font-medium">2.3 Usage Information</h3>
            <p>
              We automatically collect certain information about your device and how you interact with our service, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>IP address and device information</li>
              <li>Browser type and settings</li>
              <li>System and performance information</li>
              <li>Usage patterns and feature utilization</li>
            </ul>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">3. How We Use Your Information</h2>
            <p>We use the information we collect for the following purposes:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>To provide, maintain, and improve our fraud detection services</li>
              <li>To process and complete transactions</li>
              <li>To send administrative information, such as updates, security alerts, and support messages</li>
              <li>To respond to your comments, questions, and requests</li>
              <li>To develop new products and services</li>
              <li>To prevent fraudulent transactions and monitor against theft and other illegal activities</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">4. Data Retention and Deletion</h2>
            <p>
              We implement clear data retention policies to respect your privacy rights and comply with legal requirements.
            </p>
            
            <h3 className="text-lg font-medium">4.1 Data Retention Periods</h3>
            <p>
              By default, we retain different types of personal data for specific periods:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Account information:</strong> Retained for as long as you maintain an active account with us, plus 30 days after account closure</li>
              <li><strong>Content submitted for analysis:</strong> Retained according to your retention settings in your account preferences (defaulting to 30 days for most users)</li>
              <li><strong>Payment information:</strong> Retained for the period necessary to process payments and comply with tax and financial regulations (typically 7 years)</li>
              <li><strong>Communications:</strong> Retained for 2 years after your last interaction with our support team</li>
              <li><strong>Usage data:</strong> Aggregated after 90 days, with personally identifiable information removed</li>
            </ul>
            
            <h3 className="text-lg font-medium">4.2 Customizable Retention Controls</h3>
            <p>
              You can customize retention periods for your detection analysis results through your account settings. Available options include:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>7 days (minimum required for service functionality)</li>
              <li>30 days (default)</li>
              <li>90 days</li>
              <li>1 year</li>
              <li>Indefinite retention (until you delete them manually)</li>
            </ul>
            <p>
              These settings apply to new analyses going forward. You can change these settings at any time in your <Link to="/privacy-preferences" className="text-primary hover:underline">Privacy Preferences</Link>.
            </p>
            
            <h3 className="text-lg font-medium">4.3 Data Deletion</h3>
            <p>
              We provide you with several options to control and delete your data:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li><strong>Individual analysis deletion:</strong> You can delete specific detection results from your dashboard at any time</li>
              <li><strong>Bulk deletion:</strong> You can request deletion of all your analysis data by contacting our support team</li>
              <li><strong>Account deletion:</strong> You can delete your entire account, which will remove all associated personal information and analysis results either immediately or after a 30-day grace period</li>
            </ul>
            <p>
              To delete your account, visit your <Link to="/privacy-preferences" className="text-primary hover:underline">Privacy Preferences</Link> and select the appropriate deletion option.
            </p>
            
            <h3 className="text-lg font-medium">4.4 Retention After Deletion Requests</h3>
            <p>
              When you request deletion of your data or account:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>We will confirm receipt of your request within 3 business days</li>
              <li>Your data will be deleted from active systems within 30 days</li>
              <li>Backups containing your data will be purged within 90 days</li>
              <li>We may retain limited information for legal, regulatory, or legitimate business purposes (such as fraud prevention or financial record-keeping)</li>
            </ul>
            <p>
              Any data retained after a deletion request will be maintained with appropriate safeguards and used only for the specific legitimate purpose for which it was retained.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">5. Your Rights</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The right to access the personal information we have about you</li>
              <li>The right to request correction of inaccurate information</li>
              <li>The right to request deletion of your information</li>
              <li>The right to object to or restrict certain processing</li>
              <li>The right to data portability</li>
            </ul>
            <p>
              To exercise these rights, please contact us at privacy@fraudeye.com or visit your <Link to="/data-request" className="text-primary hover:underline">Data Request</Link> page.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">6. Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">7. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. The updated version will be indicated by an updated "Last Updated" date. We encourage you to review this Privacy Policy periodically to stay informed about how we are protecting your information.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">8. GDPR Compliance</h2>
            <p>
              For users in the European Economic Area (EEA), we comply with the General Data Protection Regulation (GDPR). This means you have certain rights regarding your personal data, including:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>The right to be informed about how we use your personal data</li>
              <li>The right to access your personal data</li>
              <li>The right to rectify inaccurate personal data</li>
              <li>The right to erasure (the "right to be forgotten")</li>
              <li>The right to restrict processing of your personal data</li>
              <li>The right to data portability</li>
              <li>The right to object to processing of your personal data</li>
              <li>Rights related to automated decision-making and profiling</li>
            </ul>
            <p className="mt-4">
              We process your data under the following legal bases:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Your consent</li>
              <li>Performance of our contract with you</li>
              <li>Compliance with legal obligations</li>
              <li>Our legitimate interests, which do not override your fundamental rights and freedoms</li>
            </ul>
            <p className="mt-4">
              To exercise any of these rights, please contact our Data Protection Officer at dpo@fraudeye.com.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">9. CCPA Compliance</h2>
            <p>
              For California residents, the California Consumer Privacy Act (CCPA) provides specific rights regarding personal information. Under the CCPA, you have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Know what personal information we collect about you</li>
              <li>Know whether your personal information is sold or disclosed and to whom</li>
              <li>Say no to the sale of your personal information</li>
              <li>Access your personal information</li>
              <li>Request deletion of your personal information</li>
              <li>Non-discrimination for exercising your CCPA rights</li>
            </ul>
            <p className="mt-4">
              Within the past 12 months, we have collected the categories of personal information described in this Privacy Policy. We do not sell your personal information as defined by the CCPA. However, we may share your information with service providers as described in this Privacy Policy.
            </p>
            <p className="mt-2">
              To exercise your rights under the CCPA, please contact us at privacy@fraudeye.com or call our toll-free number at 1-800-FRAUDEYE. Only you, or a person registered with the California Secretary of State that you authorize to act on your behalf, may make a verifiable consumer request related to your personal information.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">10. International Data Transfers</h2>
            <p>
              Your information may be transferred to, and maintained on, computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
            </p>
            <p>
              If you are located outside the United States and choose to provide information to us, please note that we transfer the information to the United States and process it there. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
            </p>
            <p>
              For transfers from the EEA to countries not considered adequate by the European Commission, we have put in place appropriate safeguards, such as standard contractual clauses adopted by the European Commission to protect your personal information.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">11. Data Retention Periods</h2>
            <p>
              We retain different types of personal data for different periods, depending on the purposes for processing and the categories of data. Here are our general retention periods:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Account information: For as long as you maintain an account with us, plus a reasonable period thereafter</li>
              <li>Payment information: For as long as needed to process payments and comply with financial regulations</li>
              <li>Communications: For as long as needed to respond to your inquiries, plus a reasonable period thereafter</li>
              <li>Marketing preferences: Until you opt-out or ask us to delete this information</li>
              <li>Analytics data: In aggregated form, for up to 26 months</li>
            </ul>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">12. Automated Data Deletion Procedures</h2>
            <p>
              Our automated data deletion procedures ensure that data is properly deleted according to our retention policies:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Automated daily scans identify data that has reached the end of its retention period</li>
              <li>Multi-stage deletion process removes data from active systems, followed by backup systems</li>
              <li>Verification processes confirm complete deletion of identified data</li>
              <li>Regular audits ensure our deletion processes are working correctly</li>
            </ul>
            <p>
              These automated processes help us maintain compliance with privacy regulations while ensuring your data isn't stored longer than necessary.
            </p>
          </section>
          
          <section className="space-y-4">
            <h2 className="text-xl font-semibold">13. Contact Us</h2>
            <p>
              If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
            </p>
            <div className="mt-2">
              <p>Email: privacy@fraudeye.com</p>
              <p>Data Protection Officer: dpo@fraudeye.com</p>
              <p>Address: 123 Fraud Detection Street, Analytics City, AC 12345</p>
              <p>Phone: 1-800-FRAUDEYE</p>
            </div>
            <div className="mt-4">
              <p>
                For EU/EEA users, if you have concerns about our data processing activities, you also have the right to lodge a complaint with your local data protection authority.
              </p>
            </div>
          </section>
          
          <section className="mt-8 border-t pt-6">
            <div className="flex justify-between items-center">
              <Link 
                to="/data-request" 
                className="text-primary hover:underline"
              >
                Request Your Data / Exercise Your Rights
              </Link>
              <Link 
                to="/privacy-preferences" 
                className="text-primary hover:underline"
              >
                Manage Privacy Preferences
              </Link>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
